import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Flower2, ShoppingBag, Palette, Gift, Sparkles, Crown, Lock,
  ChevronRight, Star, Zap, Eye, Check, AlertTriangle, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { resolveImageUrl as resolveImg } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// Cores das raridades
const RARITY_COLORS = {
  comum: "#9CA3AF",
  incomum: "#10B981",
  raro: "#3B82F6",
  epico: "#8B5CF6",
  lendario: "#F59E0B",
  mitico: "linear-gradient(90deg, #EF4444, #F59E0B, #10B981, #3B82F6, #8B5CF6)"
};

const RARITY_NAMES = {
  comum: "Comum",
  incomum: "Incomum",
  raro: "Raro",
  epico: "Épico",
  lendario: "Lendário",
  mitico: "Mítico"
};

export function GardenCollectiblesPage() {
  const [activeTab, setActiveTab] = useState('garden');
  const [garden, setGarden] = useState(null);
  const [collection, setCollection] = useState([]);
  const [shopItems, setShopItems] = useState([]);
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseDialog, setPurchaseDialog] = useState({ open: false, item: null });
  const [collectibleDialog, setCollectibleDialog] = useState({ open: false, collectible: null });
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  const [speciesData, setSpeciesData] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [gardenRes, collectionRes, shopRes, stylesRes, speciesRes] = await Promise.all([
        axios.get(`${API}/api/collectibles/my-garden`, { headers }),
        axios.get(`${API}/api/collectibles/my-collection`, { headers }),
        axios.get(`${API}/api/collectibles/shop`, { headers }),
        axios.get(`${API}/api/collectibles/styles`, { headers }),
        axios.get(`${API}/api/collectibles/species`, { headers })
      ]);

      setGarden(gardenRes.data);
      setCollection(collectionRes.data.collectibles || []);
      setShopItems(shopRes.data.items || []);
      setStyles(stylesRes.data.styles || []);
      setUserPlan(gardenRes.data.user_plan || 'free');
      setSelectedStyle(stylesRes.data.current_style);
      setSpeciesData(speciesRes.data.species || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados do jardim');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (speciesId, artStyle) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/collectibles/purchase`,
        { 
          species_id: speciesId,
          art_style: artStyle || selectedStyle,
          growth_stage: "semente"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(res.data.message);
      setPurchaseDialog({ open: false, item: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao comprar');
    }
  };

  const handleClaimGenesis = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/collectibles/founder-genesis/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reivindicar Genesis');
    }
  };

  const handleChangeStyle = async (newStyle) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/api/collectibles/styles/change`,
        { new_style: newStyle },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Estilo alterado!');
      setSelectedStyle(newStyle);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar estilo');
    }
  };

  const getRarityStyle = (rarity) => {
    if (rarity === 'mitico') {
      return { background: RARITY_COLORS[rarity], WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' };
    }
    return { color: RARITY_COLORS[rarity] };
  };

  // Build species image map from loaded data
  const speciesImageMap = {};
  speciesData.forEach(s => {
    if (s.image_url) {
      speciesImageMap[s.id] = {
        display: resolveImg(s.image_url),
        thumbnail: s.thumbnail_url ? resolveImg(s.thumbnail_url) : null,
      };
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="garden-collectibles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Flower2 className="h-6 w-6 text-green-600" strokeWidth={1.5} />
            Coleção
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Colecionáveis digitais da sua jornada
          </p>
        </div>

        {/* Stats */}
        <div className="flex gap-3">
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{garden?.stats?.total || 0}</p>
              <p className="text-xs text-green-600">Colecionáveis</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">+{garden?.garden?.total_awa_bonus || 0}%</p>
              <p className="text-xs text-amber-600">Bônus AWA</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Founder Genesis Banner */}
      {userPlan === 'founder' && !garden?.stats?.has_genesis && (
        <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0">
          <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-full">
                <Crown className="h-8 w-8" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Founder Genesis Disponível!</h3>
                <p className="text-purple-100 text-sm">
                  Como Founder, você tem direito a 1 colecionável Genesis exclusivo (Épico garantido!)
                </p>
              </div>
            </div>
            <Button 
              onClick={handleClaimGenesis}
              className="bg-white text-purple-600 hover:bg-purple-50"
            >
              <Gift className="mr-2 h-4 w-4" />
              Reivindicar Genesis
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="garden" className="flex items-center gap-2">
            <Flower2 className="h-4 w-4" />
            <span className="hidden sm:inline">Meu Jardim</span>
          </TabsTrigger>
          <TabsTrigger value="collection" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Coleção</span>
          </TabsTrigger>
          <TabsTrigger value="shop" className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Loja</span>
          </TabsTrigger>
        </TabsList>

        {/* Garden Tab */}
        <TabsContent value="garden" className="mt-6">
          <div className="space-y-6">
            {/* Style Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Estilo Artístico
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {styles.map((style) => (
                    <div
                      key={style.id}
                      onClick={() => style.accessible && handleChangeStyle(style.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedStyle === style.id
                          ? 'border-green-500 bg-green-50'
                          : style.accessible
                            ? 'border-gray-200 hover:border-green-300'
                            : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{style.name}</span>
                        {selectedStyle === style.id && (
                          <Check className="h-4 w-4 text-green-600" />
                        )}
                        {!style.accessible && (
                          <Lock className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{style.description}</p>
                      {!style.accessible && (
                        <Badge variant="secondary" className="mt-2 text-xs">
                          {style.required_plan === 'premium' ? 'Premium' : 'Founder'}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Active Collectibles Grid */}
            <Card>
              <CardHeader>
                <CardTitle>Plantas Ativas</CardTitle>
                <CardDescription>
                  Seus colecionáveis em exibição (máx. 12)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {garden?.active_collectibles?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {garden.active_collectibles.map((collectible) => (
                      <CollectibleCard
                        key={collectible.collectible_id}
                        collectible={collectible}
                        onClick={() => setCollectibleDialog({ open: true, collectible })}
                        imageUrl={speciesImageMap[collectible.species_id]?.thumbnail}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Flower2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Seu jardim está vazio</p>
                    <p className="text-sm">Compre colecionáveis na loja!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rarity Stats */}
            {garden?.stats?.by_rarity && Object.keys(garden.stats.by_rarity).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Sua Coleção por Raridade</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {Object.entries(RARITY_NAMES).map(([key, name]) => (
                      <div key={key} className="text-center p-3 rounded-lg bg-gray-50">
                        <div
                          className="text-2xl font-bold"
                          style={getRarityStyle(key)}
                        >
                          {garden.stats.by_rarity[key] || 0}
                        </div>
                        <div className="text-xs text-muted-foreground">{name}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Collection Tab */}
        <TabsContent value="collection" className="mt-6">
          {collection.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {collection.map((collectible) => (
                <CollectibleCard
                  key={collectible.collectible_id}
                  collectible={collectible}
                  onClick={() => setCollectibleDialog({ open: true, collectible })}
                  showDetails
                  imageUrl={speciesImageMap[collectible.species_id]?.display}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12 text-muted-foreground">
                <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Você ainda não tem colecionáveis</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab('shop')}
                >
                  Ir para a Loja
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Shop Tab */}
        <TabsContent value="shop" className="mt-6">
          <div className="space-y-6">
            {/* Rarity Info */}
            <Card className="bg-gradient-to-r from-gray-50 to-slate-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-sm">Raridades e Probabilidades</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'comum', prob: '50%' },
                    { key: 'incomum', prob: '25%' },
                    { key: 'raro', prob: '15%' },
                    { key: 'epico', prob: '7%' },
                    { key: 'lendario', prob: '2.5%' },
                    { key: 'mitico', prob: '0.5%' }
                  ].map(({ key, prob }) => (
                    <Badge
                      key={key}
                      variant="outline"
                      style={{ borderColor: RARITY_COLORS[key], color: RARITY_COLORS[key] }}
                    >
                      {RARITY_NAMES[key]} ({prob})
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Shop Items */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shopItems.map((item) => (
                <ShopItemCard
                  key={item.species_id}
                  item={item}
                  userPlan={userPlan}
                  onPurchase={() => setPurchaseDialog({ open: true, item })}
                  imageUrl={speciesImageMap[item.species_id]?.display}
                />
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Purchase Dialog */}
      <Dialog open={purchaseDialog.open} onOpenChange={(open) => setPurchaseDialog({ open, item: purchaseDialog.item })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {speciesImageMap[purchaseDialog.item?.species_id]?.display ? (
                <img 
                  src={speciesImageMap[purchaseDialog.item?.species_id]?.display} 
                  alt={purchaseDialog.item?.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <span className="text-2xl">{purchaseDialog.item?.emoji}</span>
              )}
              Comprar {purchaseDialog.item?.name}
            </DialogTitle>
            <DialogDescription>
              {purchaseDialog.item?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="inline h-4 w-4 mr-1" />
                A raridade será sorteada aleatoriamente. Comum é mais provável (50%).
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Preços por Raridade ({userPlan}):</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {purchaseDialog.item?.rarity_info && Object.entries(purchaseDialog.item.rarity_info).map(([key, info]) => (
                  <div key={key} className="flex justify-between p-2 bg-gray-50 rounded">
                    <span style={{ color: info.color }}>{RARITY_NAMES[key]}</span>
                    <span className="font-medium">{info.price} AWA</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPurchaseDialog({ open: false, item: null })}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handlePurchase(purchaseDialog.item?.species_id)}
              className="bg-green-600 hover:bg-green-700"
              disabled={!purchaseDialog.item?.accessible}
            >
              <Zap className="mr-2 h-4 w-4" />
              Comprar (Sorteio)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collectible Detail Dialog */}
      <Dialog open={collectibleDialog.open} onOpenChange={(open) => setCollectibleDialog({ open, collectible: collectibleDialog.collectible })}>
        <DialogContent className="max-w-lg">
          {collectibleDialog.collectible && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {speciesImageMap[collectibleDialog.collectible.species_id]?.display ? (
                    <img 
                      src={speciesImageMap[collectibleDialog.collectible.species_id]?.display} 
                      alt={collectibleDialog.collectible.attributes?.species_name}
                      className="w-16 h-16 rounded-xl object-cover ring-2"
                      style={{ ringColor: collectibleDialog.collectible.rarity_color }}
                    />
                  ) : (
                    <span className="text-2xl">
                      {PLANT_SPECIES_EMOJI[collectibleDialog.collectible.species_id] || '🌱'}
                    </span>
                  )}
                  {collectibleDialog.collectible.attributes?.species_name}
                </DialogTitle>
                <DialogDescription>
                  Serial: {collectibleDialog.collectible.serial}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Rarity Badge */}
                <div className="flex items-center gap-2">
                  <Badge
                    style={{
                      backgroundColor: collectibleDialog.collectible.rarity_color,
                      color: 'white'
                    }}
                  >
                    {RARITY_NAMES[collectibleDialog.collectible.rarity] || collectibleDialog.collectible.rarity}
                  </Badge>
                  {collectibleDialog.collectible.is_genesis && (
                    <Badge className="bg-purple-600">
                      <Crown className="h-3 w-3 mr-1" />
                      Genesis
                    </Badge>
                  )}
                </div>

                {/* Attributes */}
                <div className="grid grid-cols-2 gap-3">
                  <AttributeItem label="Estilo" value={collectibleDialog.collectible.attributes?.art_style} />
                  <AttributeItem label="Estágio" value={collectibleDialog.collectible.attributes?.growth_stage} />
                  <AttributeItem label="Fundo" value={collectibleDialog.collectible.attributes?.background} />
                  <AttributeItem label="Clima" value={collectibleDialog.collectible.attributes?.climate} />
                  <AttributeItem label="Aura" value={collectibleDialog.collectible.attributes?.aura} />
                  <AttributeItem label="Elemento Raro" value={collectibleDialog.collectible.attributes?.rare_element} />
                </div>

                {/* Benefits */}
                {collectibleDialog.collectible.benefits && (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800 mb-2">Benefícios:</p>
                    <ul className="text-sm text-green-700 space-y-1">
                      {collectibleDialog.collectible.benefits.awa_bonus > 0 && (
                        <li>+{collectibleDialog.collectible.benefits.awa_bonus}% AWA diário</li>
                      )}
                      {collectibleDialog.collectible.benefits.badge && (
                        <li>Badge: {collectibleDialog.collectible.benefits.badge}</li>
                      )}
                      {collectibleDialog.collectible.benefits.title && (
                        <li>Título: {collectibleDialog.collectible.benefits.title}</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Conquest */}
                {collectibleDialog.collectible.attributes?.achievement && (
                  <div className="text-sm text-muted-foreground">
                    <Star className="inline h-4 w-4 mr-1 text-amber-500" />
                    Conquista: {collectibleDialog.collectible.attributes.achievement.replace(/_/g, ' ')}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Emoji mapping
const PLANT_SPECIES_EMOJI = {
  lavanda: '💜', bambu: '🎋', lotus: '🪷', girassol: '🌻',
  orquidea: '🌸', cacto: '🌵', samambaia: '🌿', ipe_amarelo: '🌳',
  manjericao: '🌱', aloe_vera: '🪴', bonsai: '🌲', cerejeira: '🌸',
  arvore_enantiodromia: '🌳', flor_recomeco: '🌺', raiz_dourada: '✨',
  cristal_vivo: '💎', arvore_memoria: '🌲', flor_gratidao: '🙏',
  semente_luz: '💫', arvore_lacos: '🤝', planta_fenix: '🔥',
  flor_coragem: '🦁', raiz_calma: '🧘', semente_amanha: '🌅',
  arvore_equilibrio: '⚖️'
};

// Sub-components
function CollectibleCard({ collectible, onClick, showDetails = false, imageUrl }) {
  const emoji = PLANT_SPECIES_EMOJI[collectible.species_id] || '🌱';
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1"
      onClick={onClick}
      data-testid={`collectible-card-${collectible.species_id}`}
    >
      <CardContent className="p-4 text-center">
        {imageUrl ? (
          <div className="w-20 h-20 mx-auto rounded-xl overflow-hidden mb-2 ring-2 ring-offset-2" style={{ ringColor: collectible.rarity_color }}>
            <img 
              src={imageUrl} 
              alt={collectible.attributes?.species_name || 'Planta'} 
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div 
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl mb-2"
            style={{ backgroundColor: `${collectible.rarity_color}20` }}
          >
            {emoji}
          </div>
        )}
        <h4 className="font-medium text-sm truncate">
          {collectible.attributes?.species_name || 'Planta'}
        </h4>
        <Badge
          variant="secondary"
          className="mt-1 text-xs"
          style={{ 
            backgroundColor: `${collectible.rarity_color}20`,
            color: collectible.rarity_color
          }}
        >
          {RARITY_NAMES[collectible.rarity] || collectible.rarity}
        </Badge>
        {collectible.is_genesis && (
          <Badge className="mt-1 ml-1 text-xs bg-purple-600">Genesis</Badge>
        )}
        {showDetails && collectible.benefits?.awa_bonus > 0 && (
          <p className="text-xs text-green-600 mt-1">+{collectible.benefits.awa_bonus}% AWA</p>
        )}
      </CardContent>
    </Card>
  );
}

function ShopItemCard({ item, userPlan, onPurchase, imageUrl }) {
  const basePrice = item.prices?.comum || 800;
  
  return (
    <Card 
      className={`transition-all ${!item.accessible ? 'opacity-60' : 'hover:shadow-md'}`}
      data-testid={`shop-item-${item.species_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {imageUrl ? (
            <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 border">
              <img 
                src={imageUrl} 
                alt={item.name} 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="text-3xl shrink-0">{item.emoji}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold truncate">{item.name}</h4>
              {!item.accessible && <Lock className="h-4 w-4 text-gray-400 shrink-0" />}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
            <Badge variant="secondary" className="mt-1 text-xs">
              {item.category === 'real' ? 'Real' : 'Simbólica'}
            </Badge>
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <div>
            <span className="text-lg font-bold text-green-600">{basePrice}</span>
            <span className="text-sm text-muted-foreground ml-1">AWA+</span>
          </div>
          
          {item.accessible ? (
            <Button size="sm" onClick={onPurchase} data-testid={`buy-${item.species_id}`}>
              Comprar
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Badge variant="outline">
              {item.required_plan === 'premium' ? 'Premium' : 'Founder'}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AttributeItem({ label, value }) {
  if (!value) return null;
  return (
    <div className="p-2 bg-gray-50 rounded">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium text-sm capitalize">{value.replace(/_/g, ' ')}</p>
    </div>
  );
}

export default GardenCollectiblesPage;
